import { toSnakeCase } from "@/src/utils/analyticUtils.js";
import { apiVariants } from "@/src/constants/appConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { sidebarFeatureNames } from "@/src/constants/globalConstants.js";
import { permissionConstants } from "@/src/constants/permissionConstants.js";
import { toTitleCase } from "@/src/utils/utils.js";

export const sidebarOptionPosition = {
  TOP: "top",
  BOTTOM: "bottom",
};

export const sidebarOptionEventIDs = {
  INVITE_TEAM_MEMBER: "invite_team_member",
  INSTALL_MOBILE_APP: "install_mobile_app",
  REFER_AND_EARN: "refer_and_earn",
};

export const featureFlags = {
  customer_success_alerts: "alerts_enabled",
  sequence: "sequence_enabled",
  meeting_agent: "meeting_agent_enabled",
  calls: "calls_enabled",
  calls_warm_transfer: "calls_warm_transfer",
  parallel_dialer: "parallel_dialer_enabled",
  analytics_v2: "analytics_v2_enabled",
  sequence_analytics: "sequences_analytics_enabled",
  analytics_new_features: "analytics_new_features_enabled",
  emails: "emails_enabled",
  signals: "signals_enabled",
  agent_builder: "automations_enabled",
  sequences_email_blocks: "sequences_email_blocks_enabled",
  linkedin_blocks: "linkedin_blocks_enabled",
  sequences_voice_agent_blocks: "sequences_voice_agent_blocks_enabled",
  sequences_mr_agent_blocks: "sequences_mr_agent_blocks_enabled",
  sequences_remove_what_to_talk: "sequences_remove_what_to_talk_enabled",
  sequences_qualification_agent_blocks:
    "sequences_qualification_agent_blocks_enabled",
  sequences_threading_agent_blocks: "sequences_threading_agent_blocks_enabled",
  sequences_action_blocks: "sequences_action_blocks_enabled",
  sequences_specific_execution_time:
    "sequences_specific_execution_time_enabled",
  meeting_analytics: "meeting_analytics_enabled",
  purchasing_international_number: "purchasing_international_number_enabled",
  call_prep: "call_prep_enabled",
  light_theme: "light_theme_enabled",
  assistant: "assistant_enabled",
  select_all: "select_all_enabled",
  recommend_prospects: "recommend_prospects_enabled",
  new_zealand_numbers: "new_zealand_numbers_enabled",
  ai_variables: "ai_variables_enabled",
  call_forwarding: "call_forwarding_enabled",
  infra_forge_mailboxes: "infra_forge_mailboxes_enabled",
  conversational_ai: "conversational_ai_enabled",
  get_started_video_section: "get_started_video_section_enabled",
  saml: "saml_enabled",
  audit_trail: "audit_trail_enabled",
  phone_number_purchase: "phone_number_purchase_enabled",
  plivo: "plivo_enabled",
  plivo_parallel: "plivo_parallel_enabled",
  brand_modal: "brand_modal_enabled",
  marketing_sidebar: "marketing_enabled",
  waterfall: "waterfall_enabled",
  inbound_agent: "inbound_agent_enabled",
  dnc: "dnc_enabled",
  dynamic_crm: "dynamic_crm_enabled",
  marketing_emails: "marketing_emails_enabled",
  i_assistant: "i_assistant_enabled",
  tickets: "tickets_enabled",
  async_waterfall_single_enrichment:
    "async_waterfall_single_enrichment_enabled",
  async_waterfall_bulk_enrichment: "async_waterfall_bulk_enrichment_enabled",
  prospectV2: "prospect_v2_enabled",
  spaces: "spaces_enabled",
  projects: "projects_enabled",
  bypass_billing_restrictions: "bypass_billing_restrictions_enabled",
  standard_objects_v2: "standard_objects_v2_enabled",
  customer_success: "customer_success_enabled",
  analytics_v3: "analytics_v3_enabled",
  "ai-analytics": "analytics_v3_enabled",
  sequences_whatsapp_enabled: "sequences_whatsapp_enabled",
  analytics_editor: "analytics_editor_enabled",
  customize_layout: "customize_layout_enabled",
  alias: "alias_enabled",
  dashboards: "dashboards_enabled",
  dashboard_admin: "dashboard_admin_enabled",
  abac: "abac_enabled",
  auth_v2: "auth_v2_enabled",
  dashboard_filters: "dashboard_filters_enabled",
  sql_analytics: "sql_analytics_enabled",
  forms: "forms_enabled",
  process_design: "process_design_enabled",
  unibox: "unibox_enabled",
  chats: "chats_enabled",
  crm_lists_service: "crm_lists_service_enabled",
  huddle: "huddle_enabled",
  chat_presence: "chat_presence_enabled",
  notes: "notes_enabled",
  global_search: "global_search_enabled",
  codebase_actions: "codebase_actions_enabled",
  meeting_log_forms: "meeting_log_forms_enabled",
  embedded_chats: "embedded_chats_enabled",
  negative_credits_modal_disabled: "negative_credits_modal_disabled",
};

export const sideBarOptions = [
  {
    id: "assistant",
    text: "I Assistant",
    permission: permissionConstants.ALL,
    selected: imageNames.ASSISTANT_SELECTED,
    unselected: imageNames.ASSISTANT_UNSELECTED,
    route: "/i-assistant",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "get_started",
    text: "Get Started",
    permission: permissionConstants.ALL,
    selected: imageNames.GET_STARTED_SELECTED,
    unselected: imageNames.GET_STARTED_SELECTED,
    route: "/get_started",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "home",
    text: "Home",
    permission: permissionConstants.ALL,
    selected: imageNames.HOME_SELECTED,
    unselected: imageNames.HOME_UNSELECTED,
    route: "/home",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "dashboards",
    text: "Dashboards",
    permission: permissionConstants.ALL,
    selected: imageNames.DASHBOARDS_SELECTED,
    unselected: imageNames.DASHBOARDS_UNSELECTED,
    route: "/dashboards",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "prospect",
    text: "Prospect",
    permission: permissionConstants.ALL,
    selected: imageNames.PROSPECT_SELECTED,
    unselected: imageNames.PROSPECT_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "find_leads",
        text: "Prospect Leads",
        permission: permissionConstants.PROSPECT_LEADS,
        route: "/prospect/find_leads",
      },
      {
        id: "find_companies",
        text: "Prospect Companies",
        permission: permissionConstants.PROSPECT_COMPANIES,
        route: "/prospect/find_companies",
      },
      {
        id: "signals",
        text: "Signals",
        permission: permissionConstants.ALL,
        route: "/signals",
      },
      {
        id: "website_visitors",
        text: "Anonymous Website Visitor",
        permission: permissionConstants.ALL,
        route: "/website_visitors",
      },
    ],
  },
  {
    id: "data_enrichment",
    text: "Data Enrichment",
    permission: permissionConstants.ALL,
    selected: imageNames.DATA_ENRICHMENT_SELECTED,
    unselected: imageNames.DATA_ENRICHMENT_UNSELECTED,
    route: "/data_enrichments",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "engage",
    text: "Engage",
    permission: permissionConstants.ALL,
    selected: imageNames.ENGAGE_SELECTED,
    unselected: imageNames.ENGAGE_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "cold_outreach",
        text: sidebarFeatureNames.COLD_OUTREACH,
        permission: permissionConstants.AI_SDR,
        route: "/cold-outreach",
        matchPatterns: ["/cold-outreach", "/campaigns"],
      },
      {
        id: "sequence",
        text: sidebarFeatureNames.SEQUENCES,
        permission: permissionConstants.SEQUENCES,
        route: "/sequences",
      },
      {
        id: "conversations",
        text: "Conversations",
        permission: permissionConstants.CONVERSATIONS,
        route: "/conversations",
      },
      {
        id: "emails",
        text: "Emails",
        permission: [
          permissionConstants.CONVERSATIONS,
          permissionConstants.EMAILS,
        ],
        route: "/emails",
      },
      {
        id: "linkedin_blocks",
        text: "Linkedin",
        permission: permissionConstants.CONVERSATIONS,
        route: "/linkedin/logs",
      },
    ],
  },
  {
    id: "coder",
    text: "Vibe Coder",
    permission: permissionConstants.ALL,
    selected: imageNames.CODER_SELECTED,
    unselected: imageNames.CODER_UNSELECTED,
    route: `/${apiVariants.CODER}/full-stack-app`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "mobile_app",
    text: "Mobile App Builder",
    permission: permissionConstants.ALL,
    selected: imageNames.MOBILE_APP_SELECTED,
    unselected: imageNames.MOBILE_APP_UNSELECTED,
    route: `/${apiVariants.CODER}/mobile-app`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "landing_page",
    text: "Landing Page Builder",
    permission: permissionConstants.ALL,
    selected: imageNames.LANDING_PAGE_SELECTED,
    unselected: imageNames.LANDING_PAGE_UNSELECTED,
    route: `/${apiVariants.CODER}/landing-page`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "2_d_games_builder",
    text: "2D Games Builder",
    permission: permissionConstants.ALL,
    selected: imageNames.GAME_2D_SELECTED,
    unselected: imageNames.GAME_2D_UNSELECTED,
    route: `/${apiVariants.CODER}/game-2d`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "3_d_games_builder",
    text: "3D Games Builder",
    permission: permissionConstants.ALL,
    selected: imageNames.GAME_3D_SELECTED,
    unselected: imageNames.GAME_3D_UNSELECTED,
    route: `/${apiVariants.CODER}/game-3d`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "vibe_video",
    text: "Vibe Video",
    permission: permissionConstants.ALL,
    selected: imageNames.VIBE_VIDEO_SELECTED,
    unselected: imageNames.VIBE_VIDEO_UNSELECTED,
    route: `/${apiVariants.CODER}/video-gen`,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "business_phone",
    text: "Business Phone",
    permission: permissionConstants.DIALER,
    selected: imageNames.BUSINESS_PHONE_SELECTED,
    unselected: imageNames.BUSINESS_PHONE_UNSELECTED,
    route: "/business-phone/call_logs",
    alignment: sidebarOptionPosition.TOP,
    matchPatterns: ["/business-phone/call_logs"],
  },
  {
    id: "sales_dialer",
    text: "Sales Dialer",
    permission: permissionConstants.DIALER,
    selected: imageNames.SALES_DIALER_SELECTED,
    unselected: imageNames.SALES_DIALER_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "campaigns",
        text: "Campaigns",
        permission: permissionConstants.CALL_CAMPAIGNS,
        route: "/sales-dialer/campaigns",
        matchPatterns: ["/sales-dialer/campaign"],
      },
      {
        id: "call_logs",
        text: "Call Logs",
        route: "/sales-dialer/call_logs",
        matchPatterns: ["/sales-dialer/call_logs"],
      },
    ],
  },
  {
    id: "forms",
    text: "Forms",
    permission: permissionConstants.ALL,
    selected: imageNames.FORMS_SELECTED,
    unselected: imageNames.FORMS_UNSELECTED,
    route: "/forms",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "ecommerce_marketing",
    text: "Ecommerce Marketing",
    permission: permissionConstants.ALL,
    selected: imageNames.ECOMMERCE_MARKETING_SELECTED,
    unselected: imageNames.ECOMMERCE_MARKETING_UNSELECTED,
    route: "/ecom-marketing",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "invoices",
    text: "Invoices",
    selected: imageNames.INVOICES_SELECTED,
    unselected: imageNames.INVOICES_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "products",
    text: "Products",
    selected: imageNames.PRODUCTS_SELECTED,
    unselected: imageNames.PRODUCTS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "crm",
    text: "CRM",
    selected: imageNames.CRM_SELECTED,
    unselected: imageNames.CRM_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "contacts",
        text: "Contacts",
        permission: permissionConstants.CONTACTS,
        route: "/contacts",
      },
      {
        id: "leads",
        text: "Leads",
        permission: permissionConstants.LEADS,
        route: "/leads",
      },
      {
        id: "companies",
        text: "Companies",
        permission: permissionConstants.COMPANIES,
        route: "/companies",
      },
      {
        id: "deals",
        text: "Deals",
        permission: permissionConstants.DEALS,
        route: "/deals",
      },
      {
        id: "lists",
        text: "Lists",
        permission: permissionConstants.LISTS,
        route: "/list",
      },
    ],
  },
  // {
  //   id: "digital_employees",
  //   text: "Digital Employees",
  //   permission: permissionConstants.ALL,
  //   selected: imageNames.DIGITAL_EMPLOYEES_SELECTED,
  //   unselected: imageNames.DIGITAL_EMPLOYEES_UNSELECTED,
  //   route: "/digital-employees",
  //   alignment: sidebarOptionPosition.TOP,
  //   matchPatterns: ["/digital-employees"],
  // },
  {
    id: "voice_agents",
    text: "Voice Agents",
    permission: permissionConstants.ALL,
    selected: imageNames.VOICE_AGENT_SELECTED,
    unselected: imageNames.VOICE_AGENT_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "agents",
        text: "Agents",
        route: "/voice-agents/agents",
        permission: permissionConstants.VOICE_AGENTS,
      },
      {
        id: "campaign",
        text: "Campaign",
        route: "/voice-agents/campaign",
        permission: permissionConstants.VOICE_AGENT_CAMPAIGNS,
      },
      {
        id: "call_logs",
        text: "Call Logs",
        route: "/voice-agents/call-logs",
        permission: permissionConstants.VOICE_AGENT_CALL_LOGS,
      },
    ],
  },
  {
    id: "inference_router",
    text: "Inference Router",
    permission: permissionConstants.ALL,
    selected: imageNames.INFERENCE_ROUTER_SELECTED,
    unselected: imageNames.INFERENCE_ROUTER_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "model_providers",
        text: "Model Providers",
        route: "/inference-router/model-providers",
        permission: permissionConstants.ALL,
      },
      {
        id: "routing_logic",
        text: "Routing Logic",
        route: "/inference-router/routing-logic",
        permission: permissionConstants.ALL,
      },
      {
        id: "analytics",
        text: "Analytics",
        route: "/inference-router/analytics",
        permission: permissionConstants.ALL,
      },
      {
        id: "logs",
        text: "Logs",
        route: "/inference-router/logs",
        permission: permissionConstants.ALL,
      },
      {
        id: "setup",
        text: "Setup",
        route: "/inference-router/setup",
        permission: permissionConstants.ALL,
      },
    ],
  },
  {
    id: "business_receptionist",
    text: "Business Receptionist",
    permission: permissionConstants.ALL,
    selected: imageNames.BUSINESS_RECEPTIONIST_SELECTED,
    unselected: imageNames.BUSINESS_RECEPTIONIST_UNSELECTED,
    route: "/business-receptionist",
    alignment: sidebarOptionPosition.TOP,
    matchPatterns: ["/business-receptionist"],
  },
  {
    id: "customer_success",
    text: "Customer Success",
    selected: imageNames.CUSTOMER_SUCCESS_SELECTED,
    unselected: imageNames.CUSTOMER_SUCCESS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "customer_success_summary",
        text: "Summary",
        permission: permissionConstants.ALL,
        route: "/customer_success/summary",
      },
      {
        id: "customer_success_onboarding",
        text: "Onboarding",
        permission: permissionConstants.ALL,
        route: "/customer_success/onboarding",
      },
      {
        id: "customer_success_customers",
        text: "Customers",
        permission: permissionConstants.ALL,
        route: "/customer_success/customers",
      },
      {
        id: "customer_success_deals",
        text: "Deals",
        permission: permissionConstants.ALL,
        route: "/customer_success/deals",
      },
      {
        id: "customer_success_tasks",
        text: "Tasks",
        permission: permissionConstants.ALL,
        route: "/customer_success/tasks",
      },
      {
        id: "customer_success_alerts",
        text: "Alerts",
        permission: permissionConstants.ALL,
        route: "/customer_success/alerts",
      },
    ],
  },
  {
    id: "meeting_links",
    text: "Meeting Links",
    permission: permissionConstants.MEETINGS,
    selected: imageNames.MEETING_LINKS_SELECTED,
    unselected: imageNames.MEETING_LINKS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "meeting_agent",
        text: "Meeting Agents",
        permission: permissionConstants.MEETINGS,
        route: "/meeting_agent",
      },
      {
        id: "meeting_log",
        text: "Meeting Logs",
        permission: permissionConstants.MEETINGS,
        route: "/meeting_log",
      },
    ],
  },
  {
    id: "ai_meeting_notetaker",
    text: "AI Meeting Notetaker",
    permission: permissionConstants.MEETINGS,
    selected: imageNames.MEETING_NOTETAKER_SELECTED,
    unselected: imageNames.MEETING_NOTETAKER_UNSELECTED,
    route: "/ai_notetaker",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "meeting_router",
    text: "Meeting Router",
    permission: permissionConstants.MEETINGS,
    selected: imageNames.MEETING_ROUTER_SELECTED,
    unselected: imageNames.MEETING_ROUTER_UNSELECTED,
    route: "/routing_form",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "marketing_sidebar",
    text: "Marketing",
    permission: permissionConstants.ALL,
    selected: imageNames.MARKETING_SELECTED,
    unselected: imageNames.MARKETING_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "campaigns",
        text: "Campaigns",
        permission: permissionConstants.CAMPAIGNS_IN_ENGAGE,
        route: "/marketing/campaigns",
        matchPatterns: ["/marketing/campaigns"],
      },
    ],
  },
  {
    id: "projects",
    text: "Projects",
    permission: permissionConstants.ALL,
    selected: imageNames.PROJECTS_SELECTED,
    unselected: imageNames.PROJECTS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "tasks",
        text: "Tasks",
        route: "/spaces/projects/tasks",
        permission: permissionConstants.ALL,
      },
      {
        id: "chats",
        text: "Chats",
        route: "/projects/chats",
        permission: permissionConstants.CHATS,
      },
      {
        id: "notes",
        text: "Notes",
        route: "/projects/notes",
        permission: permissionConstants.ALL,
      },
    ],
  },
  {
    id: "unibox",
    text: "Unibox",
    permission: permissionConstants.ALL,
    selected: imageNames.UNIBOX_SELECTED,
    unselected: imageNames.UNIBOX_UNSELECTED,
    route: "/unibox",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "inbound_agent",
    text: "Support",
    permission: permissionConstants.ALL,
    selected: imageNames.INBOUND_AGENT_SELECTED,
    unselected: imageNames.INBOUND_AGENT_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "inbox",
        text: "Inbox",
        route: "/inbound_agent/inbox",
        permission: permissionConstants.ALL,
      },
      {
        id: "tickets",
        text: "Tickets",
        route: "/tickets",
        permission: permissionConstants.ALL,
      },
      {
        id: "knowledge_base",
        text: "Knowledge Base",
        route: "/inbound_agent/knowledge_base",
        permission: permissionConstants.ALL,
      },
      {
        id: "agent_instructions",
        text: "Agent Instructions",
        route: "/inbound_agent/instructions",
        permission: permissionConstants.ALL,
      },
      {
        id: "live_chat_widget",
        text: "LiveChat Widget",
        route: "/inbound_agent/live_chat",
        permission: permissionConstants.ALL,
      },
    ],
  },
  {
    id: "live_assist",
    text: "I Live Assist",
    permission: permissionConstants.ALL,
    selected: imageNames.LIVE_ASSIST_SELECTED,
    unselected: imageNames.LIVE_ASSIST_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "download_app",
        text: "Download App",
        route: "/live-assist/download",
        permission: permissionConstants.ALL,
      },
      {
        id: "la_templates",
        text: "Templates",
        route: "/live-assist/templates",
        permission: permissionConstants.ALL,
      },
      {
        id: "activity",
        text: "Activity",
        route: "/live-assist/activity",
        permission: permissionConstants.ALL,
      },
    ],
  },
  {
    id: "analyze",
    text: "Analyze",
    selected: imageNames.ANALYSE_SELECTED,
    unselected: imageNames.ANALYSE_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "analytics",
        text: "Analytics",
        permission: permissionConstants.ANALYTICS,
        route: "/analytics",
      },
      {
        id: "analytics_v3",
        text: "AI Analytics",
        permission: permissionConstants.ANALYTICS,
        route: "/ai-analytics",
      },
    ],
  },
  {
    id: "people",
    text: "People",
    selected: imageNames.PEOPLE_SELECTED,
    unselected: imageNames.PEOPLE_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "employees",
        text: "Employees",
        permission: permissionConstants.HR_EMPLOYEES,
        route: `/${apiVariants.PEOPLE}/employees`,
      },
      {
        id: "departments",
        text: "Departments",
        permission: permissionConstants.HR_DEPARTMENTS,
        route: `/${apiVariants.PEOPLE}/departments`,
      },
      {
        id: "teams",
        text: "Teams",
        permission: permissionConstants.HR_TEAMS,
        route: `/${apiVariants.PEOPLE}/teams`,
      },
      {
        id: "roles",
        text: "Roles",
        permission: permissionConstants.HR_ROLES,
        route: `/${apiVariants.PEOPLE}/roles`,
      },
      {
        id: "onboarding",
        text: "Onboarding",
        permission: permissionConstants.HR_ONBOARDING,
        route: `/${apiVariants.PEOPLE}/onboarding`,
      },
    ],
  },
  {
    id: "time_off",
    text: "Time Off",
    selected: imageNames.TIME_OFF_SELECTED,
    unselected: imageNames.TIME_OFF_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "requests",
        text: "Requests",
        permission: permissionConstants.HR_LEAVE_REQUESTS,
        route: `/${apiVariants.TIME_OFF}/requests`,
      },
      {
        id: "approvals",
        text: "Approvals",
        permission: permissionConstants.HR_LEAVE_APPROVALS,
        route: `/${apiVariants.TIME_OFF}/approvals`,
      },
      {
        id: "leaves",
        text: "Leaves",
        permission: permissionConstants.HR_LEAVE_TYPES,
        route: `/${apiVariants.TIME_OFF}/leaves`,
      },
      {
        id: "holidays",
        text: "Holidays",
        permission: permissionConstants.HR_HOLIDAYS,
        route: `/${apiVariants.TIME_OFF}/holidays`,
      },
    ],
  },
  {
    id: "attendance",
    text: "Attendance",
    selected: imageNames.ATTENDANCE_SELECTED,
    unselected: imageNames.ATTENDANCE_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "attendance",
        text: "Attendance",
        permission: permissionConstants.HR_ATTENDANCE,
        route: `/${apiVariants.ATTENDANCE}`,
      },
      {
        id: "employees",
        text: "Employees",
        permission: permissionConstants.HR_EMPLOYEE_MGMT,
        route: `/${apiVariants.ATTENDANCE}/employees`,
      },
      {
        id: "shift",
        text: "Shift",
        permission: permissionConstants.HR_SHIFTS,
        route: `/${apiVariants.ATTENDANCE}/shift`,
      },
      {
        id: "shift_mappings",
        text: "Shift Mappings",
        permission: permissionConstants.HR_SHIFTS,
        route: `/${apiVariants.ATTENDANCE}/shift_mappings`,
      },
    ],
  },
  {
    id: "ats",
    text: "ATS",
    selected: imageNames.ATS_SELECTED,
    unselected: imageNames.ATS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "requisitions",
        text: "Requisitions",
        permission: permissionConstants.HR_REQUISITIONS,
        route: `/${apiVariants.ATS}/requisitions`,
      },
      {
        id: "candidates",
        text: "Candidates",
        permission: permissionConstants.HR_CANDIDATES,
        route: `/${apiVariants.ATS}/candidates`,
      },
      {
        id: "job_openings",
        text: "Job Openings",
        permission: permissionConstants.HR_JOB_OPENINGS,
        route: `/${apiVariants.ATS}/job_openings`,
      },
      {
        id: "campaigns",
        text: "Campaigns",
        permission: permissionConstants.HR_CAMPAIGNS,
        route: `/${apiVariants.ATS}/campaigns`,
      },
    ],
  },
  {
    id: "perform",
    text: "Perform",
    selected: imageNames.PERFORM_SELECTED,
    unselected: imageNames.PERFORM_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "okrs",
        text: "OKRs",
        permission: permissionConstants.HR_OKRS,
        route: `/${apiVariants.PERFORM}/okrs`,
      },
      {
        id: "goals",
        text: "Goals",
        permission: permissionConstants.HR_GOALS,
        route: `/${apiVariants.PERFORM}/goals`,
      },
      {
        id: "performance",
        text: "Performance",
        permission: permissionConstants.HR_PERFORMANCE,
        route: `/${apiVariants.PERFORM}/performance`,
      },
    ],
  },
  {
    id: "approvals",
    text: "Approvals",
    permission: permissionConstants.ALL,
    selected: imageNames.APPROVALS_SELECTED,
    unselected: imageNames.APPROVALS_UNSELECTED,
    route: "/approvals",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "automate",
    text: "Automate",
    selected: imageNames.AUTOMATE_SELECTED,
    unselected: imageNames.AUTOMATE_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: apiVariants.WORKFLOWS,
        text: sidebarFeatureNames.WORKFLOWS,
        permission: permissionConstants.AGENT_BUILDER,
        route: `/${apiVariants.WORKFLOWS}`,
      },
    ],
  },
  {
    id: "process_design",
    text: "Process Design",
    permission: permissionConstants.ALL,
    selected: imageNames.PROCESS_DESIGN_SELECTED,
    unselected: imageNames.PROCESS_DESIGN_UNSELECTED,
    route: `/${apiVariants.PROCESS_FLOWS}`,
    alignment: sidebarOptionPosition.TOP,
    matchPatterns: [`/${toSnakeCase(apiVariants.PROCESS_FLOWS)}`],
  },
  {
    id: "esign",
    text: "E-Sign",
    permission: permissionConstants.ALL,
    selected: imageNames.ESIGN_SELECTED,
    unselected: imageNames.ESIGN_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "esign_agreements",
        text: "Agreements",
        route: "/esign/agreements",
        permission: permissionConstants.ALL,
      },
      {
        id: "esign_templates",
        text: "Templates",
        route: "/esign/templates",
        permission: permissionConstants.ALL,
      },
    ],
  },
  {
    id: "developer_tools",
    text: "Developer Tools",
    permission: permissionConstants.ALL,
    selected: imageNames.DEVELOPER_TOOLS_SELECTED,
    unselected: imageNames.DEVELOPER_TOOLS_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: apiVariants.CODEBASE,
        text: toTitleCase(apiVariants.CODEBASE),
        permission: permissionConstants.ALL,
        route: `/developer_tools/${apiVariants.CODEBASE}`,
      },
      {
        id: apiVariants.CODE_REVIEWER,
        text: toTitleCase(apiVariants.CODE_REVIEWER),
        permission: permissionConstants.ALL,
        route: `/developer_tools/${apiVariants.CODE_REVIEWER}`,
      },
      {
        id: "ai_qa",
        text: "AI QA",
        permission: permissionConstants.ALL,
        route: "/developer_tools/ai-qa",
      },
      {
        id: "error_logging",
        text: "Error Logging",
        permission: permissionConstants.ALL,
        route: "/developer_tools/error-logging",
      },
    ],
  },
  {
    id: "sandbox",
    text: "Sandbox",
    permission: permissionConstants.ALL,
    selected: imageNames.SANDBOX_SELECTED,
    unselected: imageNames.SANDBOX_UNSELECTED,
    route: "/sandbox",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "library",
    text: "Library",
    selected: imageNames.LIBRARY_SELECTED,
    unselected: imageNames.LIBRARY_UNSELECTED,
    alignment: sidebarOptionPosition.TOP,
    sections: [
      {
        id: "content",
        text: "Content",
        route: "/content",
      },
      {
        id: "marketing_emails",
        text: "Marketing Emails",
        route: "/email-templates",
      },
    ],
  },
  {
    id: "developer_center",
    text: "Developer Center",
    permission: permissionConstants.ALL,
    selected: imageNames.DEVELOPER_CENTER_SELECTED,
    unselected: imageNames.DEVELOPER_CENTER_UNSELECTED,
    route: "/developer-center",
    alignment: sidebarOptionPosition.TOP,
  },
  {
    id: "invite_team_member",
    text: "Invite Team Member",
    icon: imageNames.ADD_MEMBER,
    alignment: sidebarOptionPosition.BOTTOM,
    event: false,
  },
  {
    id: "install_mobile_app",
    text: "Install Mobile App",
    icon: imageNames.INSTALL_MOBILE,
    alignment: sidebarOptionPosition.BOTTOM,
    event: true,
  },
  {
    id: "refer_and_earn",
    text: "Refer & Earn",
    icon: imageNames.REFER,
    alignment: sidebarOptionPosition.BOTTOM,
    event: false,
  },
];
